import type { ReactNode } from 'react';
import type { ArticleRow, ArticleType } from '../../../shared/schemas/article';
import type { ListPatternState } from './listPatternState';
import type { InfiniteListPatternState } from './infiniteListPatternState';

export interface ListPatternProps {
  emptyMessage: string;
  fetchUrl: string;
  /** Must be referentially stable (`useCallback` or module-level). */
  filterFn?: (items: ArticleRow[]) => ArticleRow[];
  renderItems: (items: ArticleRow[]) => ReactNode;
  /** Overrides default user-facing error copy when status is `error`. */
  errorMessage?: string;
}

export interface InfiniteListPatternProps {
  emptyMessage: string;
  articleType: ArticleType;
  pageSize: number;
  /** Must be referentially stable (`useCallback` or module-level). */
  filterFn?: (items: ArticleRow[]) => ArticleRow[];
  renderItems: (items: ArticleRow[]) => ReactNode;
  /** Overrides default user-facing error copy when status is `error`. */
  errorMessage?: string;
}

export type ContentListPageShellProps = {
  title: string;
  subtitle?: ReactNode;
  leftPanel?: ReactNode;
  rightPanel?: ReactNode;
  footer?: ReactNode;
  /** When false, the bottom CTA is not mounted until main content is ready. */
  showCta?: boolean;
  /** When false, the site footer is not mounted until main content is ready. */
  showFooter?: boolean;
  /**
   * Detail routes with side panels: false = waiting, true = ready.
   * Left, main, and right panels mount and fade in together.
   */
  contentReady?: boolean;
};

type ContentListPageListBranch =
  | { children: ReactNode; listProps?: never; listState?: never; infiniteListProps?: never; infiniteListState?: never }
  | { children?: never; listProps: ListPatternProps; listState: ListPatternState; infiniteListProps?: never; infiniteListState?: never }
  | {
      children?: never;
      listProps?: never;
      listState?: never;
      infiniteListProps: InfiniteListPatternProps;
      infiniteListState: InfiniteListPatternState;
      loadMore: () => void;
    };

export type ContentListPageProps = ContentListPageShellProps & ContentListPageListBranch;

export function resolveContentListDataReady(
  listState: ListPatternState | undefined,
  infiniteListState: InfiniteListPatternState | undefined,
): boolean {
  const state = infiniteListState ?? listState;
  if (!state) return false;
  return state.status !== 'loading' && state.status !== 'idle';
}
